import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/site/Logo";

const nav = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-gray-200"
          : "bg-white/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] font-medium uppercase tracking-[0.14em] transition-colors ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-[#0A1F44] hover:text-[#D4AF37]"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+917487978020"
            data-testid="navbar-phone"
            className="inline-flex items-center gap-2 text-[13px] font-medium text-[#0A1F44] hover:text-[#D4AF37] transition-colors"
          >
            <Phone size={16} strokeWidth={1.75} />
            +91 74879 78020
          </a>
          <Link
            to="/contact"
            data-testid="navbar-cta"
            className="inline-flex items-center bg-[#0A1F44] text-white px-5 py-3 text-[13px] font-semibold uppercase tracking-wider hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors"
          >
            Get Consultation
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="md:hidden p-2 text-[#0A1F44]"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-nav"
        className={`md:hidden overflow-hidden transition-[max-height] duration-300 ease-in-out ${
          open ? "max-h-[420px] border-t border-gray-200" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5 bg-white">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium uppercase tracking-widest ${
                  isActive ? "text-[#D4AF37]" : "text-[#0A1F44]"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-cta"
            className="mt-2 inline-flex items-center justify-center bg-[#0A1F44] text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider"
          >
            Get Consultation
          </Link>
        </div>
      </div>
    </header>
  );
}
